import { useCallback, useRef } from 'react';

import UserIcon from '@mui/icons-material/PersonRounded';

import { useAccountExperience } from '#client/hooks/useAccountExperience';

import { getUserMenuConfig, USER_MENU_ITEM_IDS } from './config';
import { MenuItem as UserMenuItem } from './MenuItem';
import { UserInfo } from './UserInfo';
import { UserMenuButton, UserMenuContainer, UserMenuList } from './UserMenu.style';

import useAnchorElement from '../../hooks/useAnchorElement';

function resolveMenuItem(menuItem, enterprise) {
  const isEnterpriseLink = menuItem.id === USER_MENU_ITEM_IDS.ENTERPRISE_LINK;
  const href = isEnterpriseLink ? enterprise?.url : menuItem.href;

  if (isEnterpriseLink && !href) {
    return null;
  }

  return {
    ...menuItem,
    href,
    messageValues: { enterpriseName: enterprise?.name },
  };
}

function UserMenu() {
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const { anchorElement, onButtonClick, onMenuClose } = useAnchorElement(menuRef, buttonRef);
  const { accountExperience, enterprise } = useAccountExperience();

  const open = Boolean(anchorElement);
  const { chrome } = accountExperience;
  const userMenuItems = getUserMenuConfig(chrome.userMenuId);

  // Prevent the menu from closing when clicking the scrollbar
  const onMenuBlur = useCallback(
    (e) => {
      if (e.relatedTarget && menuRef.current.contains(e.relatedTarget)) {
        e.preventDefault();
        e.stopPropagation();
      } else {
        onMenuClose();
      }
    },
    [onMenuClose],
  );

  return (
    <>
      <UserMenuButton
        aria-controls="user-menu"
        aria-haspopup="true"
        clickstreamProps={{
          elementName: 'user_menu_button',
          elementStyle: 'icon',
        }}
        label="User menu"
        icon={<UserIcon color="inherit" />}
        onClick={onButtonClick}
        ref={buttonRef}
        variant="text"
        open={open}
      />

      <UserMenuContainer
        id="user-menu"
        ref={menuRef}
        anchorEl={anchorElement}
        onBlur={onMenuBlur}
        onClose={onMenuClose}
        open={open}
      >
        <UserInfo />

        <UserMenuList>
          {userMenuItems.map((menuItem) => {
            const item = resolveMenuItem(menuItem, enterprise);

            if (!item) {
              return null;
            }

            return <UserMenuItem key={item.id} onClick={onMenuClose} {...item} />;
          })}
        </UserMenuList>
      </UserMenuContainer>
    </>
  );
}

export default UserMenu;
